import { useEffect, useState } from 'react';
import { api, ApiError } from '../../api/client.js';
import { useToast } from '../../context/ToastContext.jsx';
import LoadingState from '../LoadingState.jsx';
import EmptyState from '../EmptyState.jsx';
import StatusBadge from '../StatusBadge.jsx';
import Modal from '../Modal.jsx';

const SELLER_TYPES = ['PUBLISHER', 'INTERMEDIARY', 'BOTH'];

function ownerLabel(row) {
  if (row.publisher_id) return { kind: 'Publisher', name: row.publisher_name ? `${row.publisher_name} (${row.publisher_domain})` : `#${row.publisher_id}` };
  return { kind: 'Inbound seat', name: row.seat_name ? `${row.seat_name} (${row.seat_seat_id})` : `#${row.inbound_seat_id}` };
}

export default function SellerAccountsTab() {
  const toast = useToast();
  const [accounts, setAccounts] = useState(null);
  const [publishers, setPublishers] = useState([]);
  const [seats, setSeats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const [accts, pubs, seatList] = await Promise.all([
        api.listSellerAccounts(),
        api.listPublishers().catch(() => []),
        api.listInboundSeats().catch(() => []),
      ]);
      setAccounts(accts);
      setPublishers(pubs);
      setSeats(seatList);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load seller accounts.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function deleteAccount(row) {
    if (!window.confirm(`Delete seller account "${row.seller_id}"? This only works if no supply_authorizations record references it.`)) return;
    try {
      await api.deleteSellerAccount(row.id);
      toast.success('Seller account deleted.');
      load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not delete seller account.');
    }
  }

  async function toggleStatus(row) {
    try {
      await api.patchSellerAccount(row.id, { status: row.status === 'active' ? 'disabled' : 'active' });
      toast.success(`${row.seller_id} ${row.status === 'active' ? 'disabled' : 'enabled'}.`);
      load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not update status.');
    }
  }

  return (
    <div className="stack">
      <section className="card">
        <div className="card__header">
          <h2 className="card__title">Seller accounts</h2>
          <button className="btn btn--primary btn--sm" onClick={() => setCreating(true)}>+ Add seller account</button>
        </div>
        <p className="card__hint">
          BidDigi's own account identities -- one per publisher or inbound seat. Only <strong>active</strong> rows
          appear in the public sellers.json below.
        </p>

        {loading && <LoadingState label="Loading seller accounts" />}
        {!loading && error && <EmptyState title="Couldn't load seller accounts" message={error} />}
        {!loading && !error && accounts && accounts.length === 0 && (
          <EmptyState title="No seller accounts yet" message="Add one for a publisher or inbound seat to have it appear in sellers.json." />
        )}
        {!loading && !error && accounts && accounts.length > 0 && (
          <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Owner</th>
                  <th>seller_id</th>
                  <th>Type</th>
                  <th>Name / Domain</th>
                  <th>Passthrough</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((row) => {
                  const owner = ownerLabel(row);
                  return (
                    <tr key={row.id}>
                      <td className="cell-title">{owner.name}<div className="cell-subtitle">{owner.kind}</div></td>
                      <td><code className="code-chip">{row.seller_id}</code></td>
                      <td className="cell-muted">{row.seller_type}</td>
                      <td className="cell-muted">{row.name || '—'} {row.domain ? `/ ${row.domain}` : ''}</td>
                      <td className="cell-muted">{row.is_passthrough ? 'Yes' : 'No'}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <label className="switch">
                          <input type="checkbox" checked={row.status === 'active'} onChange={() => toggleStatus(row)} />
                          <span className="switch__track"><span className="switch__thumb" /></span>
                        </label>
                      </td>
                      <td>
                        <button className="btn btn--ghost btn--sm" onClick={() => setEditing(row)}>Edit</button>{' '}
                        <button className="btn btn--ghost btn--sm" onClick={() => deleteAccount(row)}>Delete</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <SellersJsonViewer />

      {creating && (
        <SellerAccountModal
          publishers={publishers}
          seats={seats}
          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); load(); }}
        />
      )}
      {editing && (
        <SellerAccountModal
          publishers={publishers}
          seats={seats}
          existing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function SellerAccountModal({ publishers, seats, existing, onClose, onSaved }) {
  const isEdit = !!existing;
  const [ownerType, setOwnerType] = useState(existing ? (existing.publisher_id ? 'publisher' : 'seat') : 'publisher');
  const [ownerId, setOwnerId] = useState(existing ? String(existing.publisher_id || existing.inbound_seat_id) : '');
  const [sellerId, setSellerId] = useState(existing?.seller_id || '');
  const [sellerType, setSellerType] = useState(existing?.seller_type || 'PUBLISHER');
  const [name, setName] = useState(existing?.name || '');
  const [domain, setDomain] = useState(existing?.domain || '');
  const [isPassthrough, setIsPassthrough] = useState(!!existing?.is_passthrough);
  const [comment, setComment] = useState(existing?.comment || '');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setFormError(null);
    if (!isEdit && !ownerId) {
      setFormError(`Select a ${ownerType === 'publisher' ? 'publisher' : 'inbound seat'}.`);
      return;
    }
    if (!isEdit && !sellerId.trim()) {
      setFormError('seller_id is required.');
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await api.patchSellerAccount(existing.id, {
          seller_type: sellerType,
          name: name.trim() || null,
          domain: domain.trim() || null,
          is_passthrough: isPassthrough,
          comment: comment.trim() || null,
        });
      } else {
        await api.createSellerAccount({
          publisher_id: ownerType === 'publisher' ? Number(ownerId) : null,
          inbound_seat_id: ownerType === 'seat' ? Number(ownerId) : null,
          seller_id: sellerId.trim(),
          seller_type: sellerType,
          name: name.trim() || null,
          domain: domain.trim() || null,
          is_passthrough: isPassthrough,
          comment: comment.trim() || null,
        });
      }
      onSaved();
    } catch (err) {
      setFormError(err instanceof ApiError ? err.message : 'Could not save seller account.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title={isEdit ? `Edit ${existing.seller_id}` : 'Add seller account'} onClose={onClose}>
      <form onSubmit={handleSubmit} className="form">
        {!isEdit && (
          <>
            <div className="field">
              <span className="field__label">Owner</span>
              <div className="checkbox-row">
                <label className="field field--checkbox field--checkbox-inline">
                  <input type="radio" checked={ownerType === 'publisher'} onChange={() => { setOwnerType('publisher'); setOwnerId(''); }} />
                  <span>Publisher</span>
                </label>
                <label className="field field--checkbox field--checkbox-inline">
                  <input type="radio" checked={ownerType === 'seat'} onChange={() => { setOwnerType('seat'); setOwnerId(''); }} />
                  <span>Inbound seat</span>
                </label>
              </div>
            </div>
            <label className="field">
              <span className="field__label">{ownerType === 'publisher' ? 'Publisher' : 'Inbound seat'}</span>
              <select value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
                <option value="">Select&hellip;</option>
                {(ownerType === 'publisher' ? publishers : seats).map((o) => (
                  <option key={o.id} value={o.id}>{o.name}{ownerType === 'publisher' ? ` (${o.domain})` : ` (${o.seatId})`}</option>
                ))}
              </select>
            </label>
            <label className="field">
              <span className="field__label">seller_id</span>
              <input value={sellerId} onChange={(e) => setSellerId(e.target.value)} placeholder="e.g. 100001" autoFocus />
            </label>
          </>
        )}
        <label className="field">
          <span className="field__label">Seller type</span>
          <select value={sellerType} onChange={(e) => setSellerType(e.target.value)}>
            {SELLER_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label className="field">
          <span className="field__label">Name <span className="field__optional">(omit for a confidential seller)</span></span>
          <input value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className="field">
          <span className="field__label">Domain <span className="field__optional">(omit for a confidential seller)</span></span>
          <input value={domain} onChange={(e) => setDomain(e.target.value)} />
        </label>
        <label className="field field--checkbox">
          <input type="checkbox" checked={isPassthrough} onChange={(e) => setIsPassthrough(e.target.checked)} />
          <span>is_passthrough</span>
        </label>
        <label className="field">
          <span className="field__label">Comment <span className="field__optional">(optional)</span></span>
          <textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} />
        </label>
        {formError && <div className="form-error">{formError}</div>}
        <div className="form__actions">
          <button type="button" className="btn btn--ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn--primary" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        </div>
      </form>
    </Modal>
  );
}

// Fetches and renders the REAL, live, public /sellers.json -- not a preview of what the DB
// contains, but the actual file a downstream verifier (an SSP, an ads.txt crawler) would fetch.
function SellersJsonViewer() {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [showRaw, setShowRaw] = useState(false);

  async function fetchLive() {
    setLoading(true);
    setError(null);
    try {
      setData(await api.fetchSellersJson());
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Could not fetch sellers.json.';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <section className="card">
      <div className="card__header">
        <h2 className="card__title">Live sellers.json</h2>
        <div className="button-row">
          {data && (
            <button className="btn btn--ghost btn--sm" onClick={() => setShowRaw((v) => !v)}>
              {showRaw ? 'Table view' : 'Raw JSON'}
            </button>
          )}
          <button className="btn btn--ghost btn--sm" onClick={fetchLive} disabled={loading}>
            {loading ? 'Fetching…' : 'Fetch live sellers.json'}
          </button>
        </div>
      </div>
      <p className="card__hint">
        Fetches the actual public file, unauthenticated, exactly the way an outside verifier would
        -- confirms what's really being served, not just what the database contains.
      </p>
      {error && <div className="form-error">{error}</div>}
      {data && !showRaw && (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>seller_id</th>
                <th>Type</th>
                <th>Confidential</th>
                <th>Name</th>
                <th>Domain</th>
                <th>Passthrough</th>
              </tr>
            </thead>
            <tbody>
              {data.sellers.map((s, i) => (
                <tr key={`${s.seller_id}-${i}`}>
                  <td><code className="code-chip">{s.seller_id}</code></td>
                  <td className="cell-muted">{s.seller_type}</td>
                  <td className="cell-muted">{s.is_confidential ? 'Yes' : 'No'}</td>
                  <td className="cell-muted">{s.name || '—'}</td>
                  <td className="cell-muted">{s.domain || '—'}</td>
                  <td className="cell-muted">{s.is_passthrough ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="card__hint" style={{ padding: '0 16px 16px' }}>
            version {data.version} &middot; {data.sellers.length} seller{data.sellers.length === 1 ? '' : 's'}
          </p>
        </div>
      )}
      {data && showRaw && <pre className="diff-view">{JSON.stringify(data, null, 2)}</pre>}
    </section>
  );
}
